import { useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Tab, Card, CardContent, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { useSnackbar } from 'notistack';
import useStyles from '../../styles';
import {
  GetSectorSalinityConfigResponse,
} from '../../../../requests/api/apiTypes';
import apiClient from '../../../../requests/api/apiClient';
import { FetchError } from '../../../../views/types';
import ConversationsTab from '../../ConversationTab';
import { SALINITY_NAME } from '../../../../constants/graphs';
import useFetchApi from '../../../../hooks/useFetchApi';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

const SalinityConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('');

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = useFetchApi<GetSectorSalinityConfigResponse>({
    modelEndpoint: 'salinityConfigDetail',
    modelName: 'salinityConfig',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (!loading) {
      setSortBy(data?.salinityConfig?.sortBy || 'Salinidad');
      setIsLoading(false);
    }
  }, [data, loading]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const handleSortByChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSortBy(newValue);
    try {
      const { data: response } = await apiClient.salinityConfig.salinityConfigCreate(
        sectorId,
        { sortBy: newValue },
      );
      enqueueSnackbar(response.message, { variant: 'success' });
      // Sorting affects which sensors are displayed and their order in the chart.
      onConfigSaved?.();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [sectorId, enqueueSnackbar, onConfigSaved]);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          <Card className={classes.configCard}>
            <CardContent>
              <Box className={classes.selectFieldsContainer}>
                <TextField
                  label="Ordenar por"
                  variant="standard"
                  value={sortBy}
                  onChange={handleSortByChange}
                  select
                  className={classes.selectField}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="Salinidad">Salinidad</MenuItem>
                  <MenuItem value="Profundidad">Profundidad</MenuItem>
                </TextField>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        In progress
      </TabPanel>
      <TabPanel value="3" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={SALINITY_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default SalinityConfigContent;
